import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';

// Load the worker from the locally installed pdfjs-dist package instead of
// a CDN. The previous CDN-based approach (cdnjs.cloudflare.com) breaks
// whenever the browser/network/CSP blocks that external request — which is
// exactly what was happening here ("Failed to fetch dynamically imported
// module"). Bundling the worker via the package means Vite/Webpack resolves
// and serves it locally, with no external network dependency at runtime.
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

async function extractFromPdf(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let fullText = '';

  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    const content = await page.getTextContent();

    if (!content.items.length) {
      fullText += '\n';
      continue;
    }

    // pdf.js does NOT guarantee items are in reading order — for
    // multi-column layouts, tables, or headers placed via absolute
    // positioning, items can arrive in any order. We must sort them
    // ourselves by vertical position first (top to bottom), then by
    // horizontal position (left to right) within a line, or section
    // headers and the text after them get jumbled together.
    const items = content.items.map((item) => {
      const [, , , , x, y] = item.transform;
      return { str: item.str, x, y, width: item.width || 0, height: item.height || Math.abs(item.transform[3]) || 10 };
    });

    // Round y to absorb tiny sub-pixel jitter between glyphs that are
    // visually on the same line but report slightly different y values
    // (this was previously causing single lines to split into several,
    // which broke header detection like "PROFESSIONAL EXPERIENCE: ...").
    const LINE_Y_TOLERANCE = 3;

    items.sort((a, b) => {
      if (Math.abs(a.y - b.y) > LINE_Y_TOLERANCE) return b.y - a.y; // top to bottom
      return a.x - b.x; // left to right within the same line
    });

    let lastY = null;
    let lastItemEndX = null;
    let line = '';

    items.forEach((item) => {
      const sameLine = lastY !== null && Math.abs(item.y - lastY) <= LINE_Y_TOLERANCE;

      if (!sameLine) {
        if (line.trim()) fullText += line.trim() + '\n';
        line = '';
        lastItemEndX = null;
      }

      if (item.str.trim() === '') {
        // whitespace-only item from the PDF itself; treat as a separator
        if (line && !line.endsWith(' ')) line += ' ';
      } else {
        // Decide whether we need to insert a space before this chunk.
        // PDFs frequently split words across multiple text-run items
        // with no explicit space character between them (e.g. "Feb"
        // and "2022" rendered as two items). If there's a visible gap
        // between the end of the previous item and the start of this
        // one, insert a space; otherwise concatenate directly.
        if (lastItemEndX !== null) {
          const gap = item.x - lastItemEndX;
          const avgCharWidth = item.height ? item.height * 0.5 : 5;
          if (gap > avgCharWidth * 0.3) {
            if (!line.endsWith(' ')) line += ' ';
          }
        }
        line += item.str;
      }

      lastItemEndX = item.x + item.width;
      lastY = item.y;
    });

    if (line.trim()) fullText += line.trim() + '\n';
    fullText += '\n';
  }

  return fullText;
}

async function extractFromDocx(file) {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return result.value || '';
}

/**
 * Extracts plain text from a File (.pdf, .doc, .docx).
 * Throws if the file type is unsupported or extraction fails.
 */
export async function extractTextFromFile(file) {
  const name = (file.name || '').toLowerCase();

  if (name.endsWith('.pdf')) {
    return extractFromPdf(file);
  }
  if (name.endsWith('.docx')) {
    return extractFromDocx(file);
  }
  if (name.endsWith('.doc')) {
    // Legacy .doc (binary format) isn't reliably parseable in-browser.
    // mammoth only supports .docx. Surface a clear error so the UI can
    // fall back to manual entry instead of silently failing.
    throw new Error('LEGACY_DOC_UNSUPPORTED');
  }

  throw new Error('UNSUPPORTED_FILE_TYPE');
}